import { JSX, useContext, useEffect, useRef, useState } from 'react';
import { usePermissions } from 'react-admin';
import { Menu, MenuItem } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HistoryIcon from '@mui/icons-material/History';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import { ChatBotContext, MAX_RECALLED_CONVERSATIONS } from '../context/ChatBotContextProvider';
import { daiveChatModels, daiveChatModelShortNames } from '../../../defaults/defaults';
import { ChatTranscript } from './ChatTranscript';
import { ChatComposer } from './ChatComposer';
import { useFloatingPanel } from '../hooks/use-floating-panel';
import { permissionManager } from '../../permissions/manager';

/**
 * What each setting on the memory menu means, said in full.
 *
 * Written out rather than assembled from the number, because "Remember the last 1 conversations"
 * is the sort of thing that survives a review and then reads badly for years.
 */
function recallLabel(count: number): string {
    if (count === 0) {
        return 'Remember nothing';
    }
    if (count === 1) {
        return 'Remember the last conversation';
    }
    return `Remember the last ${count} conversations`;
}

export const MainChat: () => JSX.Element = (): JSX.Element => {
    const context: any = useContext(ChatBotContext);
    const { permissions } = usePermissions();
    const panel = useRef<HTMLDivElement | null>(null);
    const [recallAnchor, setRecallAnchor] = useState<HTMLElement | null>(null);
    const [modelAnchor, setModelAnchor] = useState<HTMLElement | null>(null);
    const { position, dragging, startDrag } = useFloatingPanel(panel, context?.showChatPanel === true);

    // Escape closes it, which a floating panel needs and a modal drawer got for free.
    useEffect((): (() => void) => {
        function onKeyDown(event: KeyboardEvent): void {
            if (event.key === 'Escape' && context?.showChatPanel) {
                context.toggleShowPanel(false);
            }
        }
        window.addEventListener('keydown', onKeyDown);
        return (): void => window.removeEventListener('keydown', onKeyDown);
    }, [context]);

    if (!context) {
        return null;
    }

    const botName: string = context.connection?.botName ?? 'Daive';
    const connected: boolean = context.websocket !== null;
    const recall: number = context.recallConversations ?? 0;
    const activity: string = context.activity ?? '';
    const chatModel: string = context.chatModel ?? '';
    const modelName: string = daiveChatModels.find(
        (model): boolean => model.id === chatModel
    )?.name ?? chatModel;

    /**
     * The word under the assistant's name in the header.
     *
     * It only ever said connecting, ready or disconnected, all of which describe the socket. The
     * socket is not what anyone is waiting on once the panel is open, so a header that reads
     * "ready" while a question has been sitting unanswered for ten seconds is telling the reader
     * about the wrong thing.
     */
    function headerState(): string {
        if (context.loadingOperator) {
            return 'connecting';
        }
        if (!connected) {
            return 'disconnected';
        }
        if (activity === 'thinking') {
            return 'thinking';
        }
        if (activity === 'answering') {
            return 'replying';
        }
        return 'ready';
    }

    function togglePanel(): void {
        context.toggleShowPanel(!context.showChatPanel);
    }

    // Using the assistant is its own ACL resource, added 2026-09-10 with HQAPI-82. Separate from
    // what it may read once open: HQ-API checks every lookup against the asking admin's own
    // grants, so an admin with the assistant and nothing else gets one that can read nothing.
    // Hidden rather than disabled, because the ticket endpoint refuses too and a button that
    // could only fail is worse than no button.
    if (!permissionManager.isAllowed(permissions, 'HQ_System::assistant')) {
        return <></>;
    }

    return (
        <>
            <button
                type="button"
                className="appbar__chat-button"
                onClick={ togglePanel }
                title={ botName + ' - assistant' }
                aria-label={ 'Open ' + botName }
            >
                <span className="appbar__chat-icon" />
            </button>

            {/*
              * A floating panel rather than a drawer, and deliberately not modal: the assistant
              * needs to work alongside the screen underneath rather than covering it behind a
              * backdrop. Kept mounted while open so a drag does not interrupt a streaming reply.
              */}
            { context.showChatPanel && (
                <div
                    ref={ panel }
                    className={ 'chat__panel' + (dragging ? ' is-dragging' : '') }
                    style={ position === null
                        ? { visibility: 'hidden' }
                        : { left: position.x + 'px', top: position.y + 'px' } }
                    role="dialog"
                    aria-label={ botName + ' assistant' }
                >
                    <header className="chat__header" onPointerDown={ startDrag }>
                        <DragIndicatorIcon className="header__grip" fontSize="small" />
                        <span className="header__avatar"><SmartToyOutlinedIcon fontSize="small" /></span>
                        <span className="header__identity">
                            <span className="header__name">{ botName }</span>
                            <span
                                className={ 'header__state'
                                    + (connected ? ' is-live' : '')
                                    + (activity !== '' ? ' is-busy' : '') }
                            >
                                { headerState() }
                            </span>
                        </span>
                        {/*
                          * Whether opening the panel brings the last conversation back. Off by
                          * default: opening the assistant is more often a new question than a
                          * continuation of one from yesterday.
                          */}
                        <button
                            type="button"
                            className={ 'header__resume' + (context.resumeConversation ? ' is-on' : '') }
                            onClick={ (): void => context.toggleResumeConversation() }
                            title={ context.resumeConversation
                                ? 'Opening this panel brings the last conversation back. Click to start fresh instead.'
                                : 'Opening this panel starts a new conversation. Click to carry the last one over instead.' }
                            aria-label="Carry the conversation over when this panel is reopened"
                            aria-pressed={ context.resumeConversation === true }
                        >
                            <HistoryIcon fontSize="small" />
                        </button>
                        {/*
                          * How many finished conversations the assistant may be asked about.
                          * Separate from the resume toggle beside it and easily confused with it:
                          * resume decides whether *this* conversation carries across a closed
                          * panel, this decides how far back it can be asked about at all.
                          */}
                        <button
                            type="button"
                            className={ 'header__recall' + (recall > 0 ? ' is-on' : '') }
                            onClick={ (event): void => setRecallAnchor(event.currentTarget) }
                            title={ recall === 0
                                ? 'Past conversations are not kept. Click to change how many are.'
                                : `${botName} can be asked about the last ${recall === 1 ? 'conversation' : recall + ' conversations'}. Click to change.` }
                            aria-label="How many past conversations the assistant remembers"
                            aria-haspopup="menu"
                        >
                            <PsychologyOutlinedIcon fontSize="small" />
                            <span className="header__recall-count">{ recall }</span>
                        </button>
                        <Menu
                            anchorEl={ recallAnchor }
                            open={ recallAnchor !== null }
                            onClose={ (): void => setRecallAnchor(null) }
                            anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
                            transformOrigin={ { vertical: 'top', horizontal: 'right' } }
                        >
                            { Array.from({ length: MAX_RECALLED_CONVERSATIONS + 1 },
                                (_unused, count: number): JSX.Element => (
                                    <MenuItem
                                        key={ count }
                                        selected={ count === recall }
                                        onClick={ (): void => {
                                            context.setRecallConversations(count);
                                            setRecallAnchor(null);
                                        } }
                                    >
                                        { recallLabel(count) }
                                    </MenuItem>
                                )) }
                        </Menu>
                        {/*
                          * Which model answers, and what it costs. The four differ by an order of
                          * magnitude on price and the assistant's bill is almost entirely input,
                          * so this is the control that matters most for spend - which is why the
                          * chosen one is named in the header rather than hidden behind the icon.
                          */}
                        <button
                            type="button"
                            className="header__model"
                            onClick={ (event): void => setModelAnchor(event.currentTarget) }
                            title={ 'Answering on ' + modelName + '. Click to change.' }
                            aria-label="Which model answers"
                            aria-haspopup="menu"
                        >
                            <MemoryOutlinedIcon fontSize="small" />
                            <span className="header__model-name">
                                { daiveChatModelShortNames[chatModel] ?? chatModel }
                            </span>
                        </button>
                        <Menu
                            anchorEl={ modelAnchor }
                            open={ modelAnchor !== null }
                            onClose={ (): void => setModelAnchor(null) }
                            anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
                            transformOrigin={ { vertical: 'top', horizontal: 'right' } }
                        >
                            { daiveChatModels.map((model): JSX.Element => (
                                <MenuItem
                                    key={ model.id }
                                    selected={ model.id === chatModel }
                                    onClick={ (): void => {
                                        context.setChatModel(model.id);
                                        setModelAnchor(null);
                                    } }
                                >
                                    { model.name }
                                </MenuItem>
                            )) }
                        </Menu>
                        <button
                            type="button"
                            className="header__restart"
                            onClick={ (): void => context.startNewConversation() }
                            title="Start a new conversation"
                            aria-label="Start a new conversation"
                            disabled={ !connected }
                        >
                            <RestartAltIcon fontSize="small" />
                        </button>
                        <button
                            type="button"
                            className="header__close"
                            onClick={ togglePanel }
                            title="Close"
                            aria-label="Close the assistant"
                        >
                            <CloseIcon fontSize="small" />
                        </button>
                    </header>

                    <ChatTranscript
                        messages={ context.chatMessages }
                        botName={ botName }
                        loading={ context.loadingOperator }
                        errorMessage={ context.errorMessage }
                        activity={ context.activity ?? '' }
                    />

                    <ChatComposer
                        key={ context.conversations }
                        disabled={ context.thinking || !connected }
                        busy={ context.thinking }
                        transcribing={ context.transcribing === true }
                        transcript={ context.transcript }
                        onSend={ (message: string): void => context.sendMessageToOperator(message) }
                        onRecord={ (audio: string, mime: string): void =>
                            context.sendRecordingToOperator(audio, mime) }
                        onStop={ (): void => context.stopReply() }
                        stopping={ context.stopping === true }
                    />
                </div>
            ) }
        </>
    );
};
